/**
 * DataModalTrigger - Clickable button to open structured data modal
 *
 * Renders as a styled button/link within chat messages that, when clicked,
 * opens the GenericDataModal with the associated structured data.
 *
 * Features:
 * - Visual indicator with icon and label
 * - Completeness badge
 * - Hover and click animations
 * - Integrates seamlessly with chat message styling
 */

import React, { useState } from 'react'
import type { StructuredData } from '../../types/structuredData'
import { calculateCompleteness } from '../../types/structuredData'
import { GenericDataModal } from './GenericDataModal'
import { DynamicIcon, Icon } from '@/design-system/atoms/Icon'

interface DataModalTriggerProps {
  data: StructuredData
  className?: string
}

export const DataModalTrigger: React.FC<DataModalTriggerProps> = ({
  data,
  className = '',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const completeness = calculateCompleteness(data.fields)

  const iconName = data.triggerIconName ?? data.iconName
  const legacyIcon = data.triggerIcon ?? data.icon
  const defaultIconName = data.type === 'helpdesk' ? 'ticket' : 'file-text'
  const label = data.triggerLabel || `Ver dados: ${data.title}`

  const getStatusColor = () => {
    if (completeness.percentage === 100) {
      return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30'
    }
    if (completeness.requiredMissing > 0) {
      return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30'
    }
    return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
  }

  const getStatusBadgeColor = () => {
    if (completeness.percentage === 100) {
      return 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300'
    }
    if (completeness.requiredMissing > 0) {
      return 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300'
    }
    return 'bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300'
  }

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={`
          inline-flex items-center gap-3 px-4 py-3 rounded-xl border-2
          transition-all duration-200 cursor-pointer
          active:scale-[0.98] select-none
          ${getStatusColor()}
          ${className}
        `}
        data-testid="data-modal-trigger"
      >
        {/* Icon */}
        <span className="flex-shrink-0">
          <DynamicIcon
            name={iconName ?? defaultIconName}
            fallbackValue={legacyIcon}
            size={24}
          />
        </span>

        {/* Label and status */}
        <div className="flex-1 text-left">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {label}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Clique para ver os detalhes
          </div>
        </div>

        {/* Status badge */}
        <div
          className={`
          px-2 py-1 rounded-full text-xs font-medium flex-shrink-0
          ${getStatusBadgeColor()}
        `}
        >
          {completeness.percentage}%
        </div>

        {/* Arrow icon */}
        <Icon
          name="chevron-right"
          size={20}
          className="text-gray-400 dark:text-gray-500 flex-shrink-0"
        />
      </button>

      {/* Modal */}
      <GenericDataModal
        data={data}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  )
}

export default DataModalTrigger
